import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GlobeAltIcon,
  CpuChipIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

export default function ServicesSection() {
  const { t } = useTranslation('home');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      titleKey: "services.items.websiteDevelopment.title",
      descriptionKey: "services.items.websiteDevelopment.description",
      Icon: GlobeAltIcon,
      gradient: "from-blue-500 to-cyan-400",
      link: "/services/website-development",
    },
    {
      titleKey: "services.items.webAppDevelopment.title",
      descriptionKey: "services.items.webAppDevelopment.description",
      Icon: CpuChipIcon,
      gradient: "from-purple-500 to-pink-400",
      link: "/services/web-application-development",
    },
    {
      titleKey: "services.items.seoPerformance.title",
      descriptionKey: "services.items.seoPerformance.description",
      Icon: RocketLaunchIcon,
      gradient: "from-primary to-secondary",
      link: "/services/seo-performance",
    },
  ];

  return (
    <div
      ref={sectionRef}
      id="services-section"
      className="py-24 px-6 sm:px-12 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            {t('services.badge')}
          </span>
          <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl">
            {t('services.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">{t('services.titleHighlight')}</span>
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            {t('services.description')}
          </p>
        </div>

        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
          {services.map((service, index) => {
            const CardContent = (
              <>
                {/* Hover gradient border effect */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-[2px] -z-10"
                     style={{ background: `linear-gradient(135deg, var(--tw-gradient-stops))` }}>
                  <div className="absolute inset-[2px] bg-white rounded-3xl"></div>
                </div>

                {/* Icon container */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <service.Icon className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300">
                  {t(service.titleKey)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(service.descriptionKey)}
                </p>

                {/* Colored border glow for clickable cards */}
                {service.link && (
                  <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-primary/50 group-hover:shadow-[0_0_20px_rgba(203,96,64,0.3)] transition-all duration-300 pointer-events-none"></div>
                )}

                {/* Subtle shadow on hover */}
                <div className="absolute inset-0 rounded-3xl shadow-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-20"></div>
              </>
            );

            const cardClasses = `group relative p-8 bg-white rounded-3xl transition-all duration-500 hover:-translate-y-2 ${service.link ? 'cursor-pointer' : ''}`;
            const cardStyle = {
              transitionDelay: `${index * 100}ms`,
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              boxShadow: '0 4px 20px -5px rgba(0,0,0,0.1)',
            };

            return service.link ? (
              <Link
                key={index}
                to={service.link}
                className={cardClasses}
                style={cardStyle}
              >
                {CardContent}
              </Link>
            ) : (
              <div
                key={index}
                className={cardClasses}
                style={cardStyle}
              >
                {CardContent}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
